import { B, D } from "@mobily/ts-belt";
import Fuse from "fuse.js";
import PropTypes from "prop-types";
import * as React from "react";
import VirtualizedList from "../../../theme/Virtualized/VirtualizedList";
import s from "./FilterSection.module.css";
import { Skeleton } from "../../../themeV2";

const ROW_HEIGHT = 40;

const ChevronIcon = ({ open }) => (
  <svg
    className={`${s.chevron}${open ? ` ${s.chevronOpen}` : ""}`}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

ChevronIcon.propTypes = {
  open: PropTypes.bool.isRequired,
};

export default function FilterSection({
  expandedByDefault = false,
  getOptionLabel,
  getOptionValue,
  label,
  maxHeight = 7.5,
  options,
  search = true,
  selected,
  setSelected,
}) {
  const [isOpen, setIsOpen] = React.useState(expandedByDefault);
  const [value, setValue] = React.useState("");

  const filteredOptions = React.useMemo(() => {
    const searchValue = value.trim().toLowerCase().replaceAll(/\W/g, "");

    if (!searchValue) {
      return options;
    }

    const fuse = new Fuse(options, {
      isCaseSensitive: false,
      getFn(obj) {
        return getOptionLabel(obj);
      },
      keys: [""],
      distance: 50,
      threshold: 0.4,
    });

    const results = fuse.search(searchValue);

    return results.map(D.get("item"));
  }, [options, value, getOptionLabel]);

  const listHeight = Math.max(
    ROW_HEIGHT,
    Math.min(filteredOptions.length * ROW_HEIGHT, maxHeight * ROW_HEIGHT),
  );

  return (
    <div className={s.section}>
      <button
        type="button"
        className={s.headerBtn}
        onClick={() => setIsOpen(B.not)}
        aria-expanded={isOpen}
      >
        <ChevronIcon open={isOpen} />
        {label}
      </button>

      <div className={`${s.collapse}${isOpen ? ` ${s.collapseOpen}` : ""}`}>
        <div className={s.collapseInner}>
          {search && (
            <input
              className={s.searchInput}
              type="text"
              placeholder={`Search ${label}`}
              aria-label={`Search ${label}`}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onBlur={(e) => setValue(e.target.value.trim())}
            />
          )}

          <div className={s.listContainer} style={{ minHeight: listHeight }}>
            {filteredOptions.length === 0 ? (
              <p className={s.emptyText}>
                No results for &ldquo;{value}&rdquo;
              </p>
            ) : (
              <VirtualizedList
                rowHeight={ROW_HEIGHT}
                defaultWidth={200}
                defaultHeight={listHeight}
                rows={filteredOptions}
                rowRenderer={({ index, key, style }) => {
                  const option = filteredOptions[index];
                  const optionValue = getOptionValue(option);
                  const optionLabel = getOptionLabel(option);
                  const inputId = `filter-${label}-${optionValue}`;

                  return (
                    <div className={s.row} key={key} style={style}>
                      <input
                        id={inputId}
                        className={s.checkbox}
                        type="checkbox"
                        checked={!!selected[optionValue]}
                        onChange={(e) => {
                          setSelected({
                            value: e.target.checked,
                            key: optionValue,
                          });
                        }}
                      />
                      <label htmlFor={inputId} className={s.checkboxLabel}>
                        {optionLabel}
                      </label>
                    </div>
                  );
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

FilterSection.propTypes = {
  expandedByDefault: PropTypes.bool,
  getOptionLabel: PropTypes.func.isRequired,
  getOptionValue: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  maxHeight: PropTypes.number,
  options: PropTypes.array.isRequired,
  search: PropTypes.bool,
  selected: PropTypes.object.isRequired,
  setSelected: PropTypes.func.isRequired,
};

export function FilterSectionSkeleton({ width = "60%" }) {
  return (
    <div className={s.section} aria-hidden="true">
      <div className={s.headerBtn} style={{ pointerEvents: "none" }}>
        <Skeleton
          variant="rect"
          style={{
            width: "1.25rem",
            height: "1.25rem",
            borderRadius: "var(--shape-xs)",
            flexShrink: 0,
          }}
        />
        <Skeleton variant="text" style={{ width }} />
      </div>
    </div>
  );
}

FilterSectionSkeleton.propTypes = {
  width: PropTypes.string,
};
